import { CREATE_NEWS_POST, TAKE_NEWS_POST_ACTION} from 'api/wenjetso/graphql/mutations'
import { GET_NEWS_POST_LIST, GET_NEWS_POST } from 'api/wenjetso/graphql/queries'

// eslint-disable-next-line import/no-cycle
import { authStoreId, companyStoreId } from 'stores'
import _ from 'lodash'
import moment from 'moment'

/**
 * News Store
 * 
 * @typedef {object} NewsStore
 * @property {NewsState} state - News state
 * @property {NewsAction} actions - News actions
 * */


/**
 * News State
 * 
 * @typedef {object} NewsState
 * @property {object} profile - News profile
 */
const initState = {
  page: null,
  newsRecord: {
    records: [],
    total: 0
  }
}

const actionTypes = {
  setNews: 'setNews',
  addNews: 'addNews'
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.setNews: {
      return {
        page: action.payload ? action.payload.page : null,
        newsRecord: action.payload ? action.payload.record : {
          records: [],
          total: 0
        },
      }
    }
    case actionTypes.addNews: {
      if (state.page !== 1) {
        return state
      }
      state.newsRecord.records.unshift(action.news)
      // eslint-disable-next-line no-param-reassign
      state.newsRecord.total += 1

      return {
        ...state,
      }
    }
    default:
      return state
  }
}

/**
 * News Getters
 * 
 * @typedef {object} NewsGetter
 * @property {Function} newsRecord - Get News Record
 */

/** @type {import('libs/store').StoreGettersInterface} */
// eslint-disable-next-line func-names
const getters = function ({state}) {
  this.currentPage = () => state.page
  this.getNewsRecord = () => state.newsRecord
  return this
}

/**
 * News Actions
 * 
 * @typedef {object} Newsction
 * @property {Function} fetchNewsProfile - Fetch News profile
 */

 /** @type {import('libs/store').StoreActionsInterface} */
// eslint-disable-next-line func-names
const actions = function ({ dispatch, plugins, state, useStore }) {
  const { requestGraphQL, requestGraphQLFiles } = plugins
  const setNews = (payload) => {
    dispatch({type : actionTypes.setNews, payload})
  }

  const addNews = (news) => {
    dispatch({type : actionTypes.addNews, news})
  }

  this.clear = () => {
    setNews(null)
  }

  this.fetchNewsList = async ({page, limit}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const companyStore = useStore(companyStoreId)
    const response = await requestGraphQL(GET_NEWS_POST_LIST, {
      company_id: companyStore.getters.getCompanyProfile()._id,
      page,
      limit,
    }, authStore.getters.getAccessToken())
    if (!response.error) {
      const payload = response.list_news
      setNews({
        page,
        record: payload
      })
    }
    return response
  }

  this.pauseNews = async ({newId}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(TAKE_NEWS_POST_ACTION, {
      input: {
        news_id: newId,
        action: 'Pause',
      }
    }, authStore.getters.getAccessToken())
    return response
  }

  this.resumeNews = async ({newId}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(TAKE_NEWS_POST_ACTION, {
      input: {
        news_id: newId,
        action: 'Resume',
      }
    }, authStore.getters.getAccessToken())
    return response
  }

  this.fetchNews = async ({newId}) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    const response = await requestGraphQL(GET_NEWS_POST, {
      news_id: newId,
    }, authStore.getters.getAccessToken())
    return response
  }
 
  this.createNewsPost = async (newsData, files) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const authStore = useStore(authStoreId)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const companyStore = useStore(companyStoreId)

    const inputValue = _.omitBy({
      company_id: companyStore.getters.getCompanyProfile()._id,
      title: newsData.title,
      category_codes: newsData.categoryShortCodes,
      tags: newsData.tags,
      keyword_codes: newsData.keywordShortCodes,
      promote_start_at: newsData.promoteStartAt,
      promote_end_at: newsData.promoteEndAt,
      publish_at: newsData.publishAt,
      url: newsData.url,
      description: newsData.description,
      location_ids: newsData.locationIds,
      meta_codes: newsData.metaCodes
    }, _.isNil)
    if (Array.isArray(inputValue.tags) && inputValue.tags.length === 0) {
      delete inputValue.tags
    }
    if (Array.isArray(inputValue.location_ids) && inputValue.location_ids.length === 0) {
      delete inputValue.location_ids
    }
    if (Array.isArray(inputValue.meta_codes) && inputValue.meta_codes.length === 0) {
      delete inputValue.meta_codes
    }
    if (Array.isArray(inputValue.keyword_codes) && inputValue.keyword_codes.length === 0) {
      delete inputValue.keyword_codes
    }
    const response = await requestGraphQLFiles(CREATE_NEWS_POST, {
      input: inputValue
    }, {
      fieldName: 'files',
      files
    }, authStore.getters.getAccessToken(), {
      format: 'formData'
    })
    if (!response.error) {
      const payload = response.create_news_post
      addNews(payload)
    }
    return response
  }

  return this
}

 /**
  * @type {import('libs/store').StoreInterface}
  */
export const newsStore = {
  initState,
  reducer,
  getters,
  actions,
}